import { useEffect, useRef, useState } from "react";
import { JoystickIcon } from "@/assets/icons/JoystickIcon";
import {
  isAsciiPresetName,
  usePTZSession,
  PTZ_SPEED_MAX,
  PTZ_SPEED_MIN,
} from "@/hooks/usePTZSession";
import { useRemPx } from "@/hooks/useRemPx";
import { PTZJoystick } from "./PTZJoystick";

/** 조이스틱 지름. 5.5rem = FHD 에서 88px — 해상도가 오르면 함께 커진다 */
const JOYSTICK_REM = 5.5;

interface PTZControlProps {
  /** 제어 대상 CCTV id — POST /cctvs/{id}/ptz */
  cctvId: number;
  /** 패널이 넘지 않아야 할 높이(px). 타일이 짧을 때 밖으로 넘쳐 나가는 것을 막는다 */
  maxHeight?: number;
}

/**
 * FAB + Popover 방식의 카메라 PTZ 제어 패널 — **넓은 타일용**.
 *
 * 좁은 타일에는 `PTZControlCompact` 가 뜬다. 어느 쪽을 띄울지는 `VideoPanel` 이
 * 영상 영역의 **실측 가로세로**로 정한다. 제어 로직은 `usePTZSession` 으로 공유한다.
 */
export function PTZControl({ cctvId, maxHeight }: PTZControlProps) {
  const [open, setOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const pxPerRem = useRemPx();

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  const {
    speed,
    setSpeed,
    error,
    setDesiredPanTilt,
    setDesiredZoom,
    presets,
    presetsLoading,
    presetBusy,
    gotoPreset,
    savePreset,
    deletePreset,
  } = usePTZSession(cctvId, open);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        fabRef.current &&
        !fabRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [open]);

  async function handlePresetSave() {
    await savePreset(presetName);
    setPresetName("");
  }

  return (
    <div className="relative">
      {/* Popover 패널 */}
      {open && (
        <div
          ref={panelRef}
          className="absolute bottom-[4.5rem] right-0 flex w-[17.5rem] flex-col overflow-y-auto rounded-xl bg-white/90 shadow-[0_4px_24px_rgba(0,0,0,0.5)] backdrop-blur-md dark:bg-[#1A1D27]/90"
          style={{ border: "1px solid rgba(255,255,255,0.08)", maxHeight }}
        >
          {/* 헤더 */}
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 text-xs font-semibold text-neutral-600 dark:text-white/70">
            <JoystickIcon size={14} />
            카메라 제어
          </div>

          {/* 섹션 1: PTZ 조이스틱 + Zoom */}
          <div className="flex items-center gap-3 border-t border-neutral-200 dark:border-white/5 px-4 py-4">
            {/* 조이스틱 — 방향키·WASD 동시 지원 (입력 필드 포커스 중엔 훅이 무시) */}
            <div className="flex flex-1 justify-center">
              <PTZJoystick
                size={Math.round(JOYSTICK_REM * pxPerRem)}
                onDirectionChange={setDesiredPanTilt}
              />
            </div>

            {/* 우측: Zoom 컨트롤 — 누르는 동안 continuous 줌, 떼면 정지 */}
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-xs font-medium text-neutral-500 dark:text-white/40">ZOOM</span>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-neutral-100 dark:bg-[#252833] text-neutral-600 dark:text-white/70 transition-colors hover:bg-neutral-200 dark:hover:bg-[#2A2D3A] hover:text-neutral-900 dark:hover:text-white active:bg-brand/30"
                onPointerDown={() => setDesiredZoom(1)}
                onPointerUp={() => setDesiredZoom(0)}
                onPointerLeave={() => setDesiredZoom(0)}
                onPointerCancel={() => setDesiredZoom(0)}
                title="Zoom In (누르고 있는 동안)"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" d="M12 6v12M6 12h12" />
                </svg>
              </button>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-neutral-100 dark:bg-[#252833] text-neutral-600 dark:text-white/70 transition-colors hover:bg-neutral-200 dark:hover:bg-[#2A2D3A] hover:text-neutral-900 dark:hover:text-white active:bg-brand/30"
                onPointerDown={() => setDesiredZoom(-1)}
                onPointerUp={() => setDesiredZoom(0)}
                onPointerLeave={() => setDesiredZoom(0)}
                onPointerCancel={() => setDesiredZoom(0)}
                title="Zoom Out (누르고 있는 동안)"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" d="M6 12h12" />
                </svg>
              </button>
            </div>
          </div>

          {/* 섹션 2: PTZ 속도 */}
          <div className="border-t border-neutral-200 dark:border-white/5 px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-neutral-500 dark:text-white/40">속도</span>
              <span className="text-xs font-medium text-brand">{speed}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-neutral-400 dark:text-white/30">느림</span>
              <input
                type="range"
                min={PTZ_SPEED_MIN}
                max={PTZ_SPEED_MAX}
                step={1}
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-neutral-100 dark:bg-[#252833] accent-brand [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand"
              />
              <span className="text-xs text-neutral-400 dark:text-white/30">빠름</span>
            </div>
          </div>

          {/* 섹션 3: 프리셋 — 저장된 위치로 카메라 이동 */}
          <div className="border-t border-neutral-200 dark:border-white/5 px-4 py-3">
            <span className="mb-2 block text-xs font-medium text-neutral-500 dark:text-white/40">
              프리셋
            </span>
            {presetsLoading ? (
              <div className="py-1 text-xs text-neutral-400 dark:text-white/30">불러오는 중…</div>
            ) : presets.length === 0 ? (
              <div className="py-1 text-xs text-neutral-400 dark:text-white/30">
                저장된 프리셋이 없습니다. 카메라를 원하는 위치로 움직인 뒤 저장하세요.
              </div>
            ) : (
              <div className="mb-2 flex max-h-[6rem] flex-wrap gap-1.5 overflow-y-auto">
                {presets.map((p) => (
                  <span key={p.token} className="group/preset relative inline-flex">
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={() => gotoPreset(p.token)}
                      title={`'${p.name}' 위치로 이동`}
                      className="max-w-[7.5rem] truncate rounded-lg bg-neutral-100 dark:bg-[#252833] px-2.5 py-1.5 pr-6 text-xs font-medium text-neutral-600 dark:text-white/70 transition-colors hover:bg-neutral-200 dark:hover:bg-[#2A2D3A] hover:text-neutral-900 dark:hover:text-white active:bg-brand/30 disabled:opacity-50"
                    >
                      {p.name}
                    </button>
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={() => deletePreset(p.token)}
                      title="프리셋 삭제"
                      aria-label={`'${p.name}' 프리셋 삭제`}
                      className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-400 dark:text-white/25 opacity-0 transition-opacity hover:text-red-400 group-hover/preset:opacity-100"
                    >
                      <svg
                        className="h-3 w-3"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* 현재 위치 저장 */}
            <div className="flex gap-1.5">
              <input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handlePresetSave()}
                placeholder="현재 위치 이름 (영문·숫자)"
                className="h-7 min-w-0 flex-1 rounded-md bg-neutral-100 dark:bg-[#252833] px-2 text-xs text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-white/25 outline-none focus:ring-1 focus:ring-brand/60"
              />
              <button
                type="button"
                disabled={!presetName.trim() || !isAsciiPresetName(presetName) || presetBusy}
                onClick={handlePresetSave}
                className="h-7 shrink-0 rounded-md bg-brand/20 px-2.5 text-xs font-medium text-brand transition-colors hover:bg-brand/30 disabled:opacity-40"
              >
                저장
              </button>
            </div>
            {presetName.length > 0 && !isAsciiPresetName(presetName) && (
              <div className="mt-1.5 text-xs text-yellow-400/80">
                카메라가 영문·숫자 이름만 지원합니다. (한글은 깨져 저장됨)
              </div>
            )}
          </div>

          {/* 제어 실패 표시 — app 에 전역 toast 가 없어 패널 내 인라인 */}
          {error && (
            <div className="border-t border-neutral-200 dark:border-white/5 px-4 py-2.5 text-xs leading-relaxed text-red-400">
              {error}
            </div>
          )}
        </div>
      )}

      {/* FAB 버튼 — PTZ 컨트롤 */}
      <div className="group relative flex items-center">
        {/* hover 시 좌측으로 튀어나오는 PTZ 북마크 탭 */}
        <div className="absolute right-[calc(100%-1rem)] flex translate-x-4 items-center opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100">
          <div className="flex items-center gap-1.5 rounded-l-full border border-r-0 border-white/30 bg-brand/20 py-1.5 pl-4 pr-6 backdrop-blur-sm">
            <span className="text-xs font-bold tracking-widest text-brand">PTZ</span>
          </div>
        </div>

        <button
          ref={fabRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          style={
            open
              ? undefined
              : {
                  animation: "ptz-glow-pulse 2s ease-in-out infinite",
                }
          }
          className={`relative z-10 flex h-14 w-14 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-200 ${
            open
              ? "bg-brand text-white border border-brand shadow-[0_0_12px_rgba(255,117,0,0.6)]"
              : "bg-brand text-white border border-white/40 backdrop-blur-sm"
          }`}
          title="카메라 PTZ 제어"
        >
          {!open && (
            <>
              <span
                className="absolute inset-0 rounded-full border border-white/60"
                style={{ animation: "ptz-border-ripple 2s ease-out infinite" }}
              />
              <span
                className="absolute inset-0 rounded-full border border-white/30"
                style={{ animation: "ptz-border-ripple2 2s ease-out infinite 0.8s" }}
              />
            </>
          )}
          <span className="relative z-10">
            <JoystickIcon size={24} />
          </span>
        </button>
      </div>
    </div>
  );
}
